import { useState, useEffect, useContext } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttList, SelectGroupPopup } from '../../../sporttia/all';
import { deleteElementFromArray } from '../../../lib/utils';

type Group = {
    id: string;
    group?: {
        id: string;
        name: string;
    };
    name?: string;
    excluding?: boolean;
};

type Props = {
    idRestriction?: string;
};

type SelectingGroupState = {
    open: boolean;
    isExcluding: boolean;
};

export default function RestrictionGroups({
    idRestriction,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [groups, setGroups] = useState<Group[]>([]);
    const [inclusiveGroups, setInclusiveGroups] = useState<Group[]>();
    const [excludedGroups, setExcludedGroups] = useState<Group[]>();
    const [openedSelectingGroup, setOpenedSelectingGroup] =
        useState<SelectingGroupState>({
            open: false,
            isExcluding: false,
        });

    const loadGroups = () => {
        cxt?.api('GET', `/bookings/restrictions/${idRestriction}/groups`, {
            params: {
                page: 1,
                rows: 300,
            },
            success: (r: { rows: Group[] }) => {
                setGroups(r.rows);
            },
        });
    };

    const addGroupToRestriction = (group: { id: string }) => {
        const params: { isExcluding?: boolean } = {};

        if (openedSelectingGroup.isExcluding) {
            params.isExcluding = openedSelectingGroup.isExcluding;
        }

        cxt?.api(
            'POST',
            `/bookings/restrictions/${idRestriction}/groups/${group.id}`,
            {
                params: {
                    ...params,
                },
                success: () => {
                    cxt?.showMessage('S', t('generic.added'));

                    loadGroups();

                    setOpenedSelectingGroup({
                        open: false,
                        isExcluding: false,
                    });
                },
            },
        );
    };

    const delGroupFromRestriction = (group: Group) => {
        if (group?.group) {
            cxt?.api(
                'DELETE',
                `/bookings/restrictions/${idRestriction}/groups/${group.group.id}`,
                {
                    success: () => {
                        cxt?.showMessage('S', t('generic.deleted'));

                        if (group.excluding) {
                            setExcludedGroups(
                                deleteElementFromArray(
                                    excludedGroups || [],
                                    group,
                                ),
                            );
                        } else {
                            setInclusiveGroups(
                                deleteElementFromArray(
                                    inclusiveGroups || [],
                                    group,
                                ),
                            );
                        }
                    },
                },
            );
        }
    };

    const filterGroups = () => {
        const inclusiveGroupsFilter: Group[] = [];
        const excludedGroupsFilter: Group[] = [];

        groups.forEach((group: any) => {
            if (group.excluding) {
                excludedGroupsFilter.push(group);
            } else {
                inclusiveGroupsFilter.push(group);
            }
        });

        setInclusiveGroups(inclusiveGroupsFilter);
        setExcludedGroups(excludedGroupsFilter);
    };

    /**
     * Restriction id changes
     */
    useEffect(() => {
        if (idRestriction) {
            loadGroups();
        }
    }, [idRestriction]);

    useEffect(() => {
        if (groups) {
            filterGroups();
        }
    }, [groups]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <SttList
                        title={t('InclusiveGroups')}
                        onDelete={delGroupFromRestriction}
                        onAdd={() =>
                            setOpenedSelectingGroup({
                                open: true,
                                isExcluding: false,
                            })
                        }
                        data={inclusiveGroups?.map((group: any) => ({
                            caption: group?.group
                                ? group.group.name
                                : group.name,
                            value: group.id,
                            objToSelect: group,
                        }))}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <SttList
                        title={t('ExcludedGroups')}
                        onDelete={delGroupFromRestriction}
                        onAdd={() => {
                            setOpenedSelectingGroup({
                                open: true,
                                isExcluding: true,
                            });
                        }}
                        data={excludedGroups?.map((group: any) => ({
                            caption: group?.group
                                ? group.group.name
                                : group.name,
                            value: group.id,
                            objToSelect: group,
                        }))}
                    />
                </Grid>
            </Grid>

            {openedSelectingGroup.open && (
                <SelectGroupPopup
                    open
                    onClose={() =>
                        setOpenedSelectingGroup({
                            open: false,
                            isExcluding: false,
                        })
                    }
                    onSelect={addGroupToRestriction as any}
                />
            )}
        </>
    );
}
